'use client'

import { cn } from '@brew/ui/cn'
import { useReducedMotion } from 'motion/react'
import Image from 'next/image'
import { Fragment, useEffect, useRef, useState } from 'react'
import {
  MK_BODY,
  MK_CAPTION,
  MK_SMALL,
} from '@/components/marketing/home/home-layout'
import type { Testimonial } from '@/lib/site/seo/testimonials-content'

/** How fast every column drifts, in pixels per second: a slow read. */
const SCROLL_SPEED = 36

/** A card plus its gap, roughly, for the duration before measuring. */
const APPROX_CARD_HEIGHT = 240

function TestimonialCard({
  testimonial,
  ariaHidden,
}: {
  testimonial: Testimonial
  ariaHidden?: boolean
}) {
  return (
    <figure
      aria-hidden={ariaHidden ? true : undefined}
      // The home card chrome, but with a deeper, softer shadow of its own:
      // the token's 6% shadow vanishes on the page's cream.
      className="flex w-full max-w-[320px] flex-col gap-5 rounded-3xl border border-mk-card-line bg-mk-card p-8 shadow-[0_12px_32px_-8px_rgba(30,30,30,0.14)]"
    >
      <blockquote className={cn(MK_BODY, 'text-mk-ink')}>
        <p>{testimonial.quote}</p>
      </blockquote>
      <figcaption className="flex items-center gap-2.5">
        <span className="relative size-10 shrink-0 overflow-clip rounded-full">
          <Image
            src={testimonial.photo.src}
            alt=""
            fill
            sizes="40px"
            className="object-cover"
          />
        </span>
        <span className="flex flex-col">
          <span className={cn(MK_SMALL, 'font-medium text-mk-ink')}>
            {testimonial.name}
          </span>
          {testimonial.role ? (
            <span className={cn(MK_CAPTION, 'text-mk-muted')}>
              {testimonial.role}
            </span>
          ) : null}
        </span>
      </figcaption>
    </figure>
  )
}

/**
 * One column scrolling upward without end: the cards twice over, the track
 * sliding by half its height on a linear loop (the shared `mk-marquee-up`
 * keyframes, so the browser's compositor drives it and a busy main thread
 * cannot make it stutter). The bottom padding equals the gap, so the seam
 * between the two copies lands exactly at -50%. Only the first copy
 * reaches assistive tech.
 */
function ScrollingColumn({
  items,
  className,
}: {
  items: ReadonlyArray<Testimonial>
  className?: string
}) {
  const trackRef = useRef<HTMLDivElement>(null)
  // Before the column is measured, assume an average card so the first
  // paint already moves at about the right pace.
  const [duration, setDuration] = useState(
    (items.length * APPROX_CARD_HEIGHT) / SCROLL_SPEED
  )

  // Every column moves at the same pixel speed whatever its height: one
  // loop covers half the track, so its duration is that half over the speed.
  useEffect(() => {
    const track = trackRef.current
    if (!track) {
      return
    }
    const observer = new ResizeObserver(() => {
      setDuration(track.scrollHeight / 2 / SCROLL_SPEED)
    })
    observer.observe(track)
    return () => observer.disconnect()
  }, [])

  return (
    // 320px, but free to shrink: three columns need 1008px and `lg` starts
    // with 896px of content.
    <div className={cn('w-[320px] min-w-0', className)}>
      <div
        ref={trackRef}
        // The CSS guard holds the first paint still, before the client
        // knows to swap in the still grid.
        className="flex animate-mk-marquee-up flex-col gap-6 pb-6 will-change-transform motion-reduce:animate-none"
        style={{ animationDuration: `${duration}s` }}
      >
        {[0, 1].map((copy) => (
          <Fragment key={copy}>
            {items.map((testimonial) => (
              <TestimonialCard
                key={`${copy}-${testimonial.name}`}
                testimonial={testimonial}
                ariaHidden={copy > 0}
              />
            ))}
          </Fragment>
        ))}
      </div>
    </div>
  )
}

/**
 * The wall of love: three columns of cards drifting upward at one slow,
 * shared speed inside a 760px window faded at both edges, as on the
 * design. One column on phones, two from `md`. Under reduced motion it is a
 * still grid of every card instead, so nothing scrolls and no card is
 * parked out of view.
 */
export function TestimonialsColumns({
  columns,
}: {
  columns: ReadonlyArray<ReadonlyArray<Testimonial>>
}) {
  const shouldReduceMotion = useReducedMotion()

  if (shouldReduceMotion) {
    return (
      <ul className="grid w-full justify-items-center gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {columns.flat().map((testimonial) => (
          <li key={testimonial.name} className="flex w-full justify-center">
            <TestimonialCard testimonial={testimonial} />
          </li>
        ))}
      </ul>
    )
  }

  return (
    <div className="flex max-h-[760px] w-full justify-center gap-6 overflow-hidden [mask-image:linear-gradient(to_bottom,transparent,black_18%,black_80%,transparent)]">
      {columns.map((items, index) => (
        <ScrollingColumn
          // biome-ignore lint/suspicious/noArrayIndexKey: the columns are a fixed layout, not a list that reorders.
          key={index}
          items={items}
          className={cn({
            'hidden md:block': index === 1,
            'hidden lg:block': index === 2,
          })}
        />
      ))}
    </div>
  )
}
